// =============================================================================
// components/discover/NftComingSoonStrip.tsx — "Top NFTs" strip (Discover hub,
// reference IMG_5878), GATED for v1: crypto/NFT surfaces are visible but
// non-functional (lib/comingSoon policy). Three gradient placeholder cards with
// a "Coming Soon" chip; tapping is NEVER a dead end — it explains the gate
// with an alert ("NFT gallery lands in CRYB v2").
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons, IoniconName } from '../ui/Icon';
import { spacing, radius, fonts } from '../../constants/theme';

interface PlaceholderCard {
  title: string;
  icon: IoniconName;
  gradient: [string, string];
}

// Deep-purple/dusk gradients: darker than the community cards so the strip
// reads as "gallery night mode", still inside the purple family.
const CARDS: PlaceholderCard[] = [
  { title: 'Genesis Drop', icon: 'diamond-outline', gradient: ['#4C3A99', '#8B5CF6'] },
  { title: 'On-chain Art', icon: 'color-palette-outline', gradient: ['#5B21B6', '#C084FC'] },
  { title: 'Rare Finds', icon: 'sparkles-outline', gradient: ['#3B2F7E', '#7C6CF0'] },
];

function showGate() {
  Alert.alert('Top NFTs — Coming Soon', 'NFT gallery lands in CRYB v2. You’re early — stay tuned. 🚀', [
    { text: 'Got it' },
  ]);
}

export default function NftComingSoonStrip() {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.strip}
    >
      {CARDS.map((card) => (
        <TouchableOpacity
          key={card.title}
          activeOpacity={0.85}
          onPress={showGate}
          accessibilityRole="button"
          accessibilityLabel={`${card.title} NFT preview, coming soon in CRYB v2`}
          style={styles.shadow}
        >
          <LinearGradient
            colors={card.gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.card}
          >
            {/* Ghost glyph gives the cover depth (same trick as Learn StepCard). */}
            <Ionicons name={card.icon} size={92} color="rgba(255,255,255,0.12)" style={styles.ghost} />

            <View style={styles.comingChip}>
              <Ionicons name="time-outline" size={11} color="#FFFFFF" />
              <Text style={styles.comingChipText}>Coming Soon</Text>
            </View>

            <View style={styles.bottom}>
              <Ionicons name={card.icon} size={20} color="#FFFFFF" />
              <Text style={styles.title} numberOfLines={1}>{card.title}</Text>
              <Text style={styles.subtitle} numberOfLines={1}>CRYB v2</Text>
            </View>
          </LinearGradient>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  strip: {
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  shadow: {
    borderRadius: radius.xl,
    shadowColor: 'rgba(76,58,153,0.4)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 4,
  },
  card: {
    width: 148,
    height: 168,
    borderRadius: radius.xl,
    padding: spacing.md,
    overflow: 'hidden',
    justifyContent: 'space-between',
  },
  ghost: {
    position: 'absolute',
    right: -18,
    bottom: -12,
  },
  comingChip: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
  },
  comingChipText: {
    fontSize: 10,
    color: '#FFFFFF',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    ...fonts.bold,
  },
  bottom: {
    gap: 2,
  },
  title: {
    fontSize: 15,
    color: '#FFFFFF',
    marginTop: spacing.xs,
    ...fonts.bold,
  },
  subtitle: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.75)',
    ...fonts.medium,
  },
});
